import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify';
import { getStudentById, getStudentSubjectsById, getStudentModulesBySubject, getStudentModuleCompletedList } from '@app/services/student';
import { Button } from '@app/components';
import { useSelector } from 'react-redux';
import Select from "react-select";
import StudentDetailsCard from './StudentDetailsCard';
import { encodeId } from '@app/helpers/base64url';

const StudentModuleAssignmentList = () => {

    const location = useLocation();
    const id = (location.state as { id?: string })?.id;
    const navigate = useNavigate()
    let obj: any = {}
    const [isLoading, setLoading] = useState(false);
    const [student, setStudent] = useState(obj);
    const canAccess = useSelector((state: any) => state.auth.permissions.student);
    const studentWorkStore = useSelector((state: any) => state.studentWork);
    const [search, setSearch] = useState({
        subject_id: studentWorkStore?.subject?.id ?? '',
        module_id: ''
    });
    const [subjects, setSubjects] = useState([]);
    const [modules, setModules] = useState([]);
    const [assignedModules, setAssignedModules] = useState([]);
    const [pagination, setPagination] = useState({
        total: 0,
        currentPage: 1,
        perPage: 10,
        lastPage: 1
    });

    useEffect(() => {
        if (!id) {
            toast.error('Student ID not found');
            navigate(-1);
            return;
        }
        getStudentDetailsById()
        getStudentAllActiveSubjects(id)
    },[]);

    const getStudentDetailsById = async () => {
        try {
            setLoading(true)
            const resp = await getStudentById(parseInt(id));
            if (resp.success === true) {
                setLoading(false)
                setStudent(resp?.collections)
            } else {
                setLoading(false)
                toast.error(resp?.errors[0]?.msg)
            }
        } catch (error: any) {
            setLoading(false)
            toast.error(error?.response?.message || 'Internal server error');
        }
    };

    const getStudentAllActiveSubjects = async (id) => {
        try {
            setLoading(true)
            const resp = await getStudentSubjectsById(id)
            if (resp.success === true) {
                setLoading(false)
				const subjectsData = resp?.collections || [];
				setSubjects(subjectsData)
                
                // Set the first subject as default if no subject is selected
                if (subjectsData.length > 0 && (!search.subject_id || search.subject_id === '')) {
                    const firstSubjectId = subjectsData[0].id;
                    setSearch(prev => ({ ...prev, subject_id: firstSubjectId }));
                    fetchModulesBySubject(id, firstSubjectId);
                    fetchAssignedModules(id, firstSubjectId, '', 1);
                }
            } else {
				setLoading(false)
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
			setLoading(false)
            toast.error(error?.message || 'Internal server error');
        }
    };

    const fetchModulesBySubject = async (student_id, subject_id) => {
        try {
            const payload = {
                student_id: parseInt(student_id),
                subject_id: parseInt(subject_id),
                skip: 'mixmatch'
            };
            
            const resp = await getStudentModulesBySubject(payload);
            
            if (resp.success === true) {
                const modulesData = resp?.collections || [];
                const modulesArr = modulesData.map(m => ({
                    id: m.module_id.toString(),
                    name: `${m.module_name} [Worksheets: ${m.worksheet_from}-${m.worksheet_to}]`
                }));
                setModules(modulesArr);
            }
        } catch (error: any) {
            console.error('Error fetching modules:', error);
        }
    };

    const fetchAssignedModules = async (student_id, subject_id, module_id, page) => {
        try {
            setLoading(true)
            const params = {
                page: page,
                perPage: pagination.perPage,
                sortField: null,
                sortDirection: null,
                studentId: parseInt(student_id),
                subjectId: subject_id ? parseInt(subject_id) : null,
                moduleId: module_id || null
            };
            
            const resp = await getStudentModuleCompletedList(params);
            
            if (resp.success === true) {
                setLoading(false)
                const data = resp?.collections;
                setAssignedModules(data.data || []);
                setPagination({
                    total: parseInt(data.pagination.total),
                    currentPage: parseInt(data.pagination.currentPage),
                    perPage: parseInt(data.pagination.perPage),
                    lastPage: parseInt(data.pagination.lastPage)
                });
            } else {
                setLoading(false)
                toast.error(resp?.errors[0]?.msg)
            }
        } catch (error: any) {
            setLoading(false)
            toast.error(error?.message || 'Internal server error');
        }
    };

    const handleSubjectChange = async (subject_id) => {
        setSearch({ ...search, subject_id: subject_id, module_id: '' })
        await fetchModulesBySubject(id, subject_id)
        fetchAssignedModules(id, subject_id, '', 1)
    };

    const handleModuleChange = (selectedOption) => {
        const module_id = selectedOption ? selectedOption.value : '';
        setSearch({ ...search, module_id: module_id })
        fetchAssignedModules(id, search.subject_id, module_id, 1)
    };

    const handlePageChange = (page) => {
        fetchAssignedModules(id, search.subject_id, search.module_id, page)
    };

    const handlePerPageChange = (e) => {
        const newPerPage = parseInt(e.target.value);
        setPagination(prev => ({ ...prev, perPage: newPerPage, currentPage: 1 }));
        // Re-fetch with new perPage value
        const params = {
            page: 1,
            perPage: newPerPage,
            sortField: null,
            sortDirection: null,
            studentId: parseInt(id),
            subjectId: search.subject_id ? parseInt(search.subject_id) : null,
            moduleId: search.module_id || null
        };
        
        setLoading(true);
        getStudentModuleCompletedList(params).then(resp => {
            if (resp.success === true) {
                setLoading(false);
                const data = resp?.collections;
                setAssignedModules(data.data || []);
                setPagination({
                    total: parseInt(data.pagination.total),
                    currentPage: parseInt(data.pagination.currentPage),
                    perPage: parseInt(data.pagination.perPage),
                    lastPage: parseInt(data.pagination.lastPage)
                });
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        }).catch(error => {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        });
    };

    return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12 text-center">
                        <span style={{fontSize: '1.5rem', fontWeight: 'bold'}}>Student Module Assignment List</span>
                    </div>
                    <div className="col-md-12">
                        <StudentDetailsCard student={student} />
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Module Assignment List</h3>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-sm-6">
                                        <label>Subject</label>
                                        <select
                                            className="form-control"
                                            name="subject_id"
                                            onChange={(e) => handleSubjectChange(e.target.value)}
                                            value={search.subject_id}
                                        >
                                            {subjects.length > 0 && subjects.map((obj) => (
                                                <option key={obj.id} value={obj.id}>
                                                    {obj.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-sm-6">
                                        <label>Module</label>
                                        <Select
                                            name="module_id"
                                            placeholder="All Modules"
                                            options={modules.map(m => ({ value: m.id, label: m.name }))}
                                            onChange={handleModuleChange}
                                            value={search.module_id ? modules.map(m => ({ value: m.id, label: m.name })).find(opt => opt.value === search.module_id) : null}
                                            isLoading={isLoading}
                                            isClearable={true}
                                        />
                                    </div>
                                </div>
                                {isLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                <table className="table table-bordered">
                                    { assignedModules.length > 0 && (
                                    <thead>
                                        <tr>
                                            <th style={{ width: 10 }}>#</th>
                                            <th>Subject</th>
                                            <th>Module</th>
                                            <th>Description</th>
                                            <th>Worksheets</th>
                                        </tr>
                                    </thead>
                                    )}
                                    <tbody>
                                        {assignedModules.length > 0 && assignedModules.map((obj, index) => (
                                            <tr key={index}>
                                                <td>{(pagination.currentPage - 1) * pagination.perPage + index + 1}.</td>
                                                <td>{ obj?.subject_name }</td>
                                                <td>{ obj?.name }</td>
                                                <td>{ obj?.description }</td>
                                                <td>{ obj?.worksheets }</td>
                                            </tr>
                                        ))}
                                        {assignedModules.length == 0 && (
                                            <tr>
                                                <td className='text-center' colSpan={5}>There are no assigned modules to display</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                )}

                            </div>
                            <div className="card-footer">
                                <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        <Button className="btn btn-default btn-sm" onClick={() => navigate(-1)}>
                                            <i className="fas fa-times mr-1" />Close
                                        </Button>
                                        { student?.status == 'active' && canAccess?.['set-work'] && (
                                            <Link className="btn btn-default btn-sm" to={`/student/set-work/${encodeId(id)}`} role="button">
                                                <i className="fas fa-network-wired mr-1" />Set Student Work
                                            </Link>
                                        )}
                                        { canAccess?.['work-summary'] && (
                                            <Link className="btn btn-default btn-sm" to="/student/work-summary" state={{ id: id }} role="button">
                                                <i className="fas fa-book-reader mr-1" />Current Student Work
                                            </Link>
                                        )}
                                        { canAccess?.['work-summary'] && (
                                            <Link className="btn btn-default btn-sm" to="/student/work-summary-history" state={{ id: id }} role="button">
                                                <i className="fas fa-history mr-1" />Work Summary History
                                            </Link>
                                        )}
                                    </div>
                                {!isLoading && pagination.total > 0 && (
                                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                        <span className="text-muted" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                            {((pagination.currentPage - 1) * pagination.perPage) + 1}–{Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of {pagination.total}
                                        </span>
                                        <select 
                                            className="form-control form-control-sm"
                                            style={{width: '70px'}}
                                            value={pagination.perPage}
                                            onChange={handlePerPageChange}
                                        >
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                        {pagination.lastPage > 1 && (
                                        <ul className="pagination pagination-sm m-0">
                                            {pagination.currentPage > 1 && (
                                                <li className="page-item">
                                                    <a className="page-link" href="#" onClick={(e) => { e.preventDefault(); handlePageChange(pagination.currentPage - 1); }}>«</a>
                                                </li>
                                            )}
                                            {Array.from({ length: pagination.lastPage }, (_, i) => i + 1).map(page => (
                                                <li className="page-item" key={page}>
                                                    <a 
                                                        className="page-link" 
                                                        style={{backgroundColor: pagination.currentPage == page ? '#dee2e6' : '#fff'}} 
                                                        href="#" 
                                                        onClick={(e) => { e.preventDefault(); handlePageChange(page); }}
                                                    >
                                                        {page}
                                                    </a>
                                                </li>
                                            ))}
                                            {pagination.currentPage < pagination.lastPage && (
                                                <li className="page-item">
                                                    <a className="page-link" href="#" onClick={(e) => { e.preventDefault(); handlePageChange(pagination.currentPage + 1); }}>»</a>
                                                </li>
                                            )}
                                        </ul>
                                        )}
                                    </div>
                                )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </section>
    </div>
  )
}

export default StudentModuleAssignmentList;
